import { atom, selector, selectorFamily } from "recoil";
import { PaginatedResponse } from "../types/app";
import {
  JobCore,
  JobFilters,
  JobScheduleRequest,
  JobScheduleRequestTouched,
  JobStatusItem,
} from "../types/jobs";
import {
  JOB_SCHEDULE_DEFAULT,
  JOB_SCHEDULE_TOUCHED_DEFAULT,
  JOBS_FILTERS_DEFAULTS,
} from "../constants/jobs";

export const jobsStatusState = atom<JobStatusItem[] | undefined>({
  key: "app.jobs.status",
  default: undefined,
});

export const jobsState = atom<PaginatedResponse<JobCore> | undefined>({
  key: "app.jobs",
  default: undefined,
});

export const jobsIdsState = selector<string[] | undefined>({
  key: "app.jobs.ids",
  get: ({ get }) => {
    const jobs = get(jobsState);
    if (!jobs) return;
    return jobs.items.map((job) => job._id);
  },
});

export const jobState = selectorFamily<JobCore | undefined, string>({
  key: "app.job",
  get:
    (id) =>
    ({ get }) => {
      const jobs = get(jobsState);
      return jobs?.items.find((job) => job._id === id);
    },
});

export const searchJobsState = atom<string | undefined>({
  key: "app.jobs.search",
  default: undefined,
});

export const jobsPageState = atom({
  key: "app.jobs.page",
  default: 1,
});

export const updateJobsState = atom<string | undefined>({
  key: "app.jobs.update",
  default: undefined,
});

export const loadingJobsState = atom({
  key: "app.jobs.loading",
  default: false,
});

export const jobsFiltersState = atom<JobFilters>({
  key: "app.jobs.filters",
  default: JOBS_FILTERS_DEFAULTS,
});

export const updateJobModalState = atom<string | undefined>({
  key: "app.job.modal.update",
  default: undefined,
});

export const jobScheduleState = atom<JobScheduleRequest>({
  key: "app.job.schedule",
  default: JOB_SCHEDULE_DEFAULT,
});

export const jobScheduleTouchedState = atom<JobScheduleRequestTouched>({
  key: "app.job.schedule.touched",
  default: JOB_SCHEDULE_TOUCHED_DEFAULT,
});

export const jobScheduleValidState = selector<{
  [key in keyof JobScheduleRequest]: boolean;
}>({
  key: "app.jobSchedule.edit.valid",
  get: ({ get }) => {
    const fields = get(jobScheduleState);

    // SET ALL FIELDS TO BE VALID
    const validFields = Object.fromEntries(
      Object.keys(fields).map((key) => [key, true])
    ) as {
      [key in keyof JobScheduleRequest]: boolean;
    };

    // VALIDATE REQUIRED FIELDS
    if (!fields.userId) {
      validFields.userId = false;
    }

    if (!fields.scheduleDate) {
      validFields.scheduleDate = false;
    }

    return validFields;
  },
});

export const jobScheduleErrorState = selector({
  key: "app.jobSchedule.edit.error",
  get: ({ get }) => {
    const validFields = get(jobScheduleValidState);
    return Object.values(validFields).some((valid) => !valid);
  },
});
